import { MessageType } from '../types';

export type ToolLifecycleEventType = 'tool_call' | 'tool_output';

export const CALL_CLIENT_TOOL = 'call_client_tool';

/** Display metadata for a customer-registered browser tool, snapshotted by
 * the backend when the tool was called (CLIENT_TOOLS_PLAN.md). */
export interface ClientToolSnapshot {
  name?: string | null;
  title?: string;
  description?: string;
  annotations?: Record<string, boolean>;
  page_url?: string;
  unregistered?: boolean;
}

export interface ToolLifecycleEvent {
  type: ToolLifecycleEventType;
  call_id: string;
  tool_name: string;
  client_tool?: ClientToolSnapshot;
  created_at?: string;
  args?: any;
  output?: any;
  message_id?: string;
  conversation_id?: string;
  context_snapshot_id?: string;
}

export interface ToolLifecycleState {
  call_id: string;
  tool_name: string;
  client_tool?: ClientToolSnapshot;
  args: any;
  output?: any;
  status: 'started' | 'completed';
  started_at?: string;
  completed_at?: string;
  message_id?: string;
  output_message_id?: string;
  context_snapshot_id?: string;
}

const DISPLAYABLE_LEGACY_OUTPUT_TOOLS = new Set(['search_knowledge', 'get_company_info']);

const isObject = (value: any): value is Record<string, any> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const parseJsonIfString = (value: any): any => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const normalizeArgs = (args: any): any => {
  const parsed = parseJsonIfString(args);
  if (parsed === null || parsed === undefined) {
    return {};
  }
  return parsed;
};

const normalizeOutput = (output: any): any => {
  const parsed = parseJsonIfString(output);
  if (parsed === null || parsed === undefined) {
    return {};
  }
  return parsed;
};

export const normalizeToolLifecycleEvent = (raw: any): ToolLifecycleEvent | null => {
  if (!isObject(raw)) {
    return null;
  }

  const type = raw.type;
  if (type !== 'tool_call' && type !== 'tool_output') {
    return null;
  }

  const callId = raw.call_id;
  const toolName = raw.tool_name;
  if (typeof callId !== 'string' || !callId.trim()) {
    return null;
  }
  if (typeof toolName !== 'string' || !toolName.trim()) {
    return null;
  }

  const event: ToolLifecycleEvent = {
    type,
    call_id: callId,
    tool_name: toolName,
    created_at: typeof raw.created_at === 'string' ? raw.created_at : undefined,
    message_id: typeof raw.message_id === 'string' ? raw.message_id : undefined,
    conversation_id:
      typeof raw.conversation_id === 'string' ? raw.conversation_id : undefined,
    context_snapshot_id:
      typeof raw.context_snapshot_id === 'string' ? raw.context_snapshot_id : undefined,
  };
  if (type === 'tool_call') {
    event.args = normalizeArgs(raw.args);
    if (isObject(raw.client_tool)) {
      event.client_tool = raw.client_tool as ClientToolSnapshot;
    }
  } else {
    event.output = normalizeOutput(raw.output);
  }
  return event;
};

const getLifecycleState = (message: MessageType): ToolLifecycleState | null => {
  const metadata = (message as any)?.message_metadata;
  if (!isObject(metadata) || !isObject(metadata.tool_lifecycle)) {
    return null;
  }
  return metadata.tool_lifecycle as ToolLifecycleState;
};

const buildMessageFromEvent = (event: ToolLifecycleEvent): MessageType => {
  const state: ToolLifecycleState = {
    call_id: event.call_id,
    tool_name: event.tool_name,
    args: normalizeArgs(event.args),
    output: event.type === 'tool_output' ? normalizeOutput(event.output) : undefined,
    status: event.type === 'tool_output' ? 'completed' : 'started',
    started_at: event.created_at,
    completed_at: event.type === 'tool_output' ? event.created_at : undefined,
    message_id: event.message_id,
    output_message_id: event.type === 'tool_output' ? event.message_id : undefined,
    context_snapshot_id: event.context_snapshot_id,
  };
  if (event.client_tool) {
    state.client_tool = event.client_tool;
  }

  return {
    id: event.call_id,
    type: 'tool_call',
    role: 'assistant',
    content: '',
    created_at: event.created_at || new Date().toISOString(),
    message_metadata: {
      tool_lifecycle: state,
    },
  };
};

const mergeExistingState = (
  existing: MessageType,
  event: ToolLifecycleEvent
): MessageType => {
  const current = getLifecycleState(existing);
  const baseState: ToolLifecycleState =
    current || (buildMessageFromEvent(event).message_metadata.tool_lifecycle as ToolLifecycleState);

  const nextState: ToolLifecycleState = {
    ...baseState,
    call_id: event.call_id,
    tool_name: event.tool_name,
  };

  if (event.type === 'tool_call') {
    nextState.args = normalizeArgs(event.args);
    nextState.status = nextState.status || 'started';
    nextState.started_at = event.created_at || nextState.started_at;
    nextState.message_id = event.message_id || nextState.message_id;
    nextState.context_snapshot_id = event.context_snapshot_id || nextState.context_snapshot_id;
    nextState.client_tool = event.client_tool || nextState.client_tool;
  } else {
    nextState.output = normalizeOutput(event.output);
    nextState.status = 'completed';
    nextState.completed_at = event.created_at || nextState.completed_at;
    nextState.output_message_id = event.message_id || nextState.output_message_id;
  }

  return {
    ...existing,
    type: 'tool_call',
    role: 'assistant',
    content: existing.content || '',
    created_at:
      (event.type === 'tool_call' && event.created_at) || existing.created_at,
    message_metadata: {
      ...(isObject((existing as any).message_metadata)
        ? (existing as any).message_metadata
        : {}),
      tool_lifecycle: nextState,
    },
  };
};

export const applyToolLifecycleEvent = (
  messages: MessageType[],
  rawEvent: any
): MessageType[] => {
  const event = normalizeToolLifecycleEvent(rawEvent);
  if (!event) {
    return messages;
  }

  const index = messages.findIndex((message) => {
    const state = getLifecycleState(message);
    return state?.call_id === event.call_id;
  });

  if (index === -1) {
    return [...messages, buildMessageFromEvent(event)];
  }

  const next = [...messages];
  next[index] = mergeExistingState(messages[index], event);
  return next;
};

const legacyToolCallEventsFromMessage = (message: MessageType): ToolLifecycleEvent[] => {
  const metadata = (message as any)?.message_metadata;
  if (
    (message as any)?.role !== 'assistant' ||
    (message as any)?.type !== 'tool' ||
    !isObject(metadata) ||
    !Array.isArray(metadata.tool_calls)
  ) {
    return [];
  }

  const createdAt = (message as any)?.created_at;
  const clientToolSnapshots = isObject(metadata.client_tools) ? metadata.client_tools : {};
  const events: ToolLifecycleEvent[] = [];
  for (const toolCall of metadata.tool_calls) {
    if (!isObject(toolCall)) {
      continue;
    }
    const fn = isObject(toolCall.function) ? toolCall.function : {};
    const callId = toolCall.id;
    const toolName = fn.name;
    if (typeof callId !== 'string' || typeof toolName !== 'string') {
      continue;
    }
    events.push({
      type: 'tool_call',
      call_id: callId,
      tool_name: toolName,
      args: normalizeArgs(fn.arguments),
      created_at: createdAt,
      ...(isObject(clientToolSnapshots[callId])
        ? { client_tool: clientToolSnapshots[callId] as ClientToolSnapshot }
        : {}),
    });
  }
  return events;
};

const legacySystemFunctionCallEventsFromMessage = (
  message: MessageType
): ToolLifecycleEvent[] => {
  const metadata = (message as any)?.message_metadata;
  if (
    (message as any)?.role !== 'system' ||
    !isObject(metadata) ||
    !Array.isArray(metadata.function_calls)
  ) {
    return [];
  }

  const createdAt = (message as any)?.created_at;
  const messageId =
    typeof (message as any)?.id === 'string' && (message as any).id.length > 0
      ? (message as any).id
      : `system-fc-${createdAt || Date.now()}`;

  const events: ToolLifecycleEvent[] = [];
  metadata.function_calls.forEach((functionCall: any, index: number) => {
    if (!isObject(functionCall)) {
      return;
    }
    const callId =
      (typeof functionCall.id === 'string' && functionCall.id) ||
      (typeof functionCall.tool_call_id === 'string' && functionCall.tool_call_id) ||
      `${messageId}:${index}`;
    const toolName =
      (typeof functionCall.name === 'string' && functionCall.name) ||
      (typeof functionCall.function?.name === 'string' && functionCall.function.name) ||
      '';
    if (!toolName) {
      return;
    }
    const args = functionCall.arguments ?? functionCall.function?.arguments ?? {};
    events.push({
      type: 'tool_call',
      call_id: callId,
      tool_name: toolName,
      args: normalizeArgs(args),
      created_at: createdAt,
    });
  });

  return events;
};

const legacyToolOutputEventFromMessage = (message: MessageType): ToolLifecycleEvent | null => {
  if ((message as any)?.role !== 'tool' || (message as any)?.type !== 'tool') {
    return null;
  }

  const metadata = (message as any)?.message_metadata;
  if (!isObject(metadata)) {
    return null;
  }

  const fn = isObject(metadata.function) ? metadata.function : {};
  const callId = metadata.id;
  const toolName = fn.name;
  if (typeof callId !== 'string' || typeof toolName !== 'string') {
    return null;
  }
  const contentPayload = parseJsonIfString((message as any).content);

  // A raw browser-tool result row carries the whole stored envelope, so the
  // card can show the result and any error without a second fetch.
  if (toolName === CALL_CLIENT_TOOL && isObject(contentPayload) && contentPayload.type === 'client_tool_result') {
    return {
      type: 'tool_output',
      call_id: callId,
      tool_name: toolName,
      output: { ...contentPayload, details_included: true },
      created_at: (message as any)?.created_at,
    };
  }

  if (!DISPLAYABLE_LEGACY_OUTPUT_TOOLS.has(toolName)) {
    return null;
  }

  let output: any;
  if (toolName === 'search_knowledge') {
    const urls = Array.isArray(contentPayload?.urls)
      ? contentPayload.urls.filter((url: any) => typeof url === 'string')
      : [];
    output = { urls };
  } else {
    output = isObject(contentPayload) ? contentPayload : {};
  }

  return {
    type: 'tool_output',
    call_id: callId,
    tool_name: toolName,
    output,
    created_at: (message as any)?.created_at,
  };
};

export const mergeToolLifecycleMessages = (
  messages: MessageType[] = []
): MessageType[] => {
  return messages.reduce((acc: MessageType[], message: MessageType) => {
    const directEvent = normalizeToolLifecycleEvent(message as any);
    if (directEvent) {
      return applyToolLifecycleEvent(acc, directEvent);
    }

    const legacyCallEvents = legacyToolCallEventsFromMessage(message);
    if (legacyCallEvents.length > 0) {
      return legacyCallEvents.reduce(
        (nextMessages, event) => applyToolLifecycleEvent(nextMessages, event),
        acc
      );
    }

    const legacySystemCallEvents = legacySystemFunctionCallEventsFromMessage(message);
    if (legacySystemCallEvents.length > 0) {
      return legacySystemCallEvents.reduce(
        (nextMessages, event) => applyToolLifecycleEvent(nextMessages, event),
        acc
      );
    }

    const legacyOutputEvent = legacyToolOutputEventFromMessage(message);
    if (legacyOutputEvent) {
      return applyToolLifecycleEvent(acc, legacyOutputEvent);
    }

    if ((message as any)?.role === 'tool' && (message as any)?.type === 'tool') {
      return acc;
    }

    return [...acc, message];
  }, []);
};

export const getToolLifecycleFromMetadata = (
  metadata: any
): ToolLifecycleState | null => {
  if (!isObject(metadata)) {
    return null;
  }
  if (isObject(metadata.tool_lifecycle)) {
    return metadata.tool_lifecycle as ToolLifecycleState;
  }
  return null;
};
